import { Page } from "@/types";
import { useContext, useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { ProgressBar } from "primereact/progressbar";
import { Badge } from "primereact/badge";
import { Timeline } from "primereact/timeline";
import { Tag } from "primereact/tag";
import { LayoutContext } from "../../admin/layout/context/layoutcontext";
import Layout from '../layout/layout';
import AppBar from '../layout/AppBar';
import waterLogo from "./amanziLytics.png";
import DamsMap from "./DamsMap";
import { 
  FaTachometerAlt, 
  FaWater, 
  FaMapMarkedAlt, 
  FaChartLine, 
  FaDatabase, 
  FaSyncAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
  FaInfoCircle,
  FaFilter
} from "react-icons/fa";
import { GiWaterDrop, GiDam } from "react-icons/gi";

// Enhanced demo data
const dams = [
    { 
      name: "All Dams", 
      code: "all", 
      status: "all",
      trend: "stable"
    },
    { 
      name: "Insiza Dam", 
      code: "insiza", 
      capacity: 173, 
      inflow: 3, 
      level: 1.73,
      status: "low",
      trend: "up",
      change: 0.2
    },
    { 
      name: "Inyankuni Dam", 
      code: "inyankuni", 
      capacity: 81, 
      inflow: 3, 
      level: 3.70,
      status: "normal",
      trend: "stable",
      change: 0.0
    },
    { 
      name: "Umzingwane Dam", 
      code: "umzingwane", 
      capacity: 45, 
      inflow: 3, 
      level: 6.67,
      status: "normal",
      trend: "up",
      change: 0.1
    },
    { 
      name: "Lower Ncema", 
      code: "lncema", 
      capacity: 0, 
      inflow: 0, 
      level: 0.00,
      status: "critical",
      trend: "stable",
      change: 0.0
    },
    { 
      name: "Upper Ncema", 
      code: "uncema", 
      capacity: 45, 
      inflow: 4, 
      level: 8.89,
      status: "good",
      trend: "up",
      change: 0.3
    },
    { 
      name: "Mtshabezi Dam", 
      code: "mtshabezi", 
      capacity: 52, 
      inflow: 0, 
      level: 0.00,
      status: "critical",
      trend: "down",
      change: -0.1
    },
];

const inflowContributions = [
    { name: "Inyankuni", value: 24.08, volume: "81,000 m³", trend: "up" },
    { name: "Umzingwane", value: 22.30, volume: "45,000 m³", trend: "stable" },
    { name: "Insiza", value: 20.15, volume: "173,000 m³", trend: "up" },
    { name: "Upper Ncema", value: 18.52, volume: "45,000 m³", trend: "up" },
    { name: "Mtshabezi", value: 15.19, volume: "52,000 m³", trend: "down" },
    { name: "Lower Ncema", value: 0.00, volume: "0 m³", trend: "stable" },
];

const dailyReadings = [
    { date: "04/01/25", totalInflow: 340000, avgLevel: 3.14, trend: "up", alerts: 2 },
    { date: "03/01/25", totalInflow: 338000, avgLevel: 3.12, trend: "up", alerts: 1 },
    { date: "02/01/25", totalInflow: 335000, avgLevel: 3.10, trend: "stable", alerts: 0 },
    { date: "01/01/25", totalInflow: 333000, avgLevel: 3.08, trend: "down", alerts: 1 },
    { date: "31/12/24", totalInflow: 330000, avgLevel: 3.05, trend: "up", alerts: 0 },
];

const recentEvents = [
    { status: "warning", date: "04/01/25 14:30", message: "Mtshabezi Dam level dropped below critical threshold", dam: "Mtshabezi" },
    { status: "success", date: "04/01/25 11:15", message: "Upper Ncema inflow increased by 15%", dam: "Upper Ncema" },
    { status: "info", date: "03/01/25 16:45", message: "System maintenance completed", dam: "All Dams" },
    { status: "warning", date: "03/01/25 09:20", message: "Data sync delayed for Insiza Dam", dam: "Insiza" },
    { status: "success", date: "02/01/25 08:00", message: "Daily report generated successfully", dam: "All Dams" },
];

const DemoDashboard: Page = () => {
    const { colors } = useContext(LayoutContext);
    const [scrolled, setScrolled] = useState(false);
    const [selectedDam, setSelectedDam] = useState(dams[0]);
    const [chartData, setChartData] = useState<any>(null);
    const [lastUpdated] = useState("04/01/25 14:30");
    const [timeRange, setTimeRange] = useState("7d");

    // Handle scroll for navbar shadow
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Initialize chart data
    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const inflowData = {
            labels: ["Insiza", "Inyankuni", "Mtshabezi", "U/Ncema", "Umzingwane", "L/Ncema"],
            datasets: [
                {
                    label: "Dam Capacity (thousand m³)",
                    backgroundColor: documentStyle.getPropertyValue('--blue-500'),
                    borderColor: documentStyle.getPropertyValue('--blue-500'),
                    borderWidth: 2,
                    borderRadius: 6,
                    data: [173, 81, 52, 45, 45, 0],
                },
                {
                    label: "Cumulative Inflow (thousand m³)",
                    backgroundColor: documentStyle.getPropertyValue('--green-500'),
                    borderColor: documentStyle.getPropertyValue('--green-500'),
                    borderWidth: 2,
                    borderRadius: 6,
                    data: [3, 3, 0, 4, 3, 0],
                },
            ],
        };

        setChartData(inflowData);
    }, []);

    
    const navItems = [
        { id: 'overview', label: 'Overview', icon: 'pi pi-home' },
        { id: 'dam-analytics', label: 'Dam Analytics', icon: 'pi pi-chart-bar' },
        { id: 'inflow-trends', label: 'Inflow Trends', icon: 'pi pi-chart-line' },
        { id: 'maps', label: 'Maps', icon: 'pi pi-map' },
    ];

    const timeRanges = [
        { label: '24 Hours', value: '24h' },
        { label: '7 Days', value: '7d' },
        { label: '30 Days', value: '30d' },
        { label: '3 Months', value: '3m' },
    ];

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: colors.textColor,
                    usePointStyle: true,
                    padding: 20,
                }
            },
            tooltip: {
                backgroundColor: colors.cardBg,
                titleColor: colors.textColor,
                bodyColor: colors.lightText,
                borderColor: colors.borderColor,
                borderWidth: 1,
                padding: 12,
                cornerRadius: 6,
            }
        },
        scales: {
            x: {
                ticks: { 
                    color: colors.lightText,
                    font: { size: 12 }
                },
                grid: { 
                    color: `${colors.borderColor}40`,
                    drawBorder: false
                }
            },
            y: {
                ticks: { 
                    color: colors.lightText,
                    font: { size: 12 }
                },
                grid: { 
                    color: `${colors.borderColor}40`,
                    drawBorder: false
                },
                beginAtZero: true
            }
        }
    };

    const getDamStatusColor = (level: number | undefined) => {
        if (level === undefined) return colors.dangerColor;
        if (level >= 80) return colors.successColor;
        if (level >= 50) return colors.infoColor;
        if (level >= 20) return colors.warningColor;
        return colors.dangerColor;
    };

    const getStatusIcon = (status: string) => {
        switch(status) {
            case 'critical': return <FaExclamationTriangle className="text-red-500" />;
            case 'low': return <FaExclamationTriangle className="text-orange-500" />;
            case 'normal': return <FaCheckCircle className="text-blue-500" />;
            case 'good': return <FaCheckCircle className="text-green-500" />;
            default: return <FaInfoCircle className={colors.lightText} />;
        }
    };

    const getTrendIcon = (trend: string) => {
        switch(trend) {
            case 'up': return <FaArrowUp className="text-green-500" />;
            case 'down': return <FaArrowDown className="text-red-500" />;
            default: return <span className="text-gray-500">─</span>;
        }
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat().format(num);
    };

    const getEventStatusColor = (status: string) => {
        switch(status) {
            case 'success': return colors.successColor;
            case 'warning': return colors.warningColor;
            case 'info': return colors.infoColor;
            case 'danger': return colors.dangerColor;
            default: return colors.primaryColor;
        }
    };

    const eventTemplate = (item: any) => {
        const statusColor = getEventStatusColor(item.status);
        
        return (
            <div className="flex align-items-start gap-3 p-3 border-round-lg hover-surface"
                 style={{ 
                     backgroundColor: colors.hoverBg,
                     transition: 'all 0.2s ease'
                 }}>
                <div className="flex-shrink-0">
                    <div className="w-3 h-3 border-round mt-1" 
                         style={{ backgroundColor: statusColor }} />
                </div>
                <div className="flex-1">
                    <div className="flex justify-content-between align-items-start mb-1">
                        <span className="font-medium text-sm" style={{ color: colors.textColor }}>
                            {item.dam}
                        </span>
                        <span className="text-xs" style={{ color: colors.lightText }}>
                            {item.date}
                        </span>
                    </div>
                    <p className="text-sm m-0" style={{ color: colors.lightText }}>
                        {item.message}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="relative flex flex-column min-h-screen">
                {/* Enhanced background elements */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                    <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl"></div>
                </div>

                {/* AppBar */}
                <AppBar scrolled={scrolled} navItems={navItems} />

                {/* Main Content */}
                <div className="flex-grow-1 pt-16">
                    <div className="py-6 px-4 mx-0 md:mx-6 lg:mx-8 lg:px-8">
                        {/* Enhanced Hero Section */}
                        <div id="overview" className="grid grid-nogutter justify-content-between align-items-center mb-8 py-8 md:mb-12 md:py-12">
                            <div className="col-12 md:col-7 flex flex-column gap-4">
                                <div className="flex align-items-center gap-3">
                                    <div className="flex align-items-center gap-2">
                                        <div className="p-2 border-round-lg"
                                             style={{ 
                                                 backgroundColor: colors.primaryBg,
                                                 color: colors.primaryColor 
                                             }}>
                                            <GiDam size={20} />
                                        </div>
                                        <span className="font-bold px-3 py-1 border-round text-sm"
                                              style={{ 
                                                  backgroundColor: colors.primaryBg, 
                                                  color: colors.primaryColor 
                                              }}>
                                            INTERACTIVE DEMO
                                        </span>
                                    </div>
                                    <Badge value="LIVE" severity="danger" className="animate-pulse" />
                                </div>
                                <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl line-height-2 bg-gradient-to-r bg-clip-text text-transparent"
                                    style={{ 
                                        backgroundImage: `linear-gradient(135deg, ${colors.primaryColor}, ${colors.infoColor})`,
                                        WebkitBackgroundClip: 'text'
                                    }}>
                                    City of Bulawayo:<br />Water Intelligence Dashboard
                                </h1>
                                <p className="text-lg line-height-3 max-w-30rem" style={{ color: colors.lightText }}>
                                    Real-time monitoring, predictive analytics, and intelligent insights for 
                                    municipal water infrastructure management. Experience the future of water governance.
                                </p>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round animate-pulse" style={{ backgroundColor: colors.successColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>Live data simulation</span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round animate-pulse" style={{ backgroundColor: colors.infoColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>AI-powered analytics</span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round animate-pulse" style={{ backgroundColor: colors.warningColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>Predictive insights</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-5 mt-6 md:mt-0">
                                <div className="p-5 border-round-2xl shadow-3 hover:shadow-4 transition-all duration-300"
                                     style={{ 
                                         backgroundColor: colors.cardBg, 
                                         border: `1px solid ${colors.borderColor}`,
                                         background: `linear-gradient(145deg, ${colors.cardBg}, ${colors.surfaceBg})`
                                     }}>
                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center gap-2">
                                            <i className="pi pi-clock" style={{ color: colors.primaryColor, fontSize: '1rem' }}></i>
                                            <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                Data Last Updated
                                            </h3>
                                        </div>
                                        <Button 
                                            icon="pi pi-sync" 
                                            className="p-button-rounded p-button-text"
                                            style={{ color: colors.primaryColor }}
                                            tooltip="Refresh Data"
                                        />
                                    </div>
                                    <div className="text-center py-3">
                                        <div className="text-6xl font-bold mb-2" style={{ color: colors.primaryColor }}>6</div>
                                        <div className="text-sm font-medium mb-1" style={{ color: colors.textColor }}>
                                            Active Dams Monitored
                                        </div>
                                        <div className="text-xs" style={{ color: colors.lightText }}>
                                            Last sync: {lastUpdated}
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-top-1" style={{ borderColor: colors.borderColor }}>
                                        <div className="flex justify-content-between align-items-center">
                                            <span className="text-sm" style={{ color: colors.lightText }}>System Status</span>
                                            <Tag value="Operational" severity="success" rounded />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Key Metrics Section */}
                        <div id="dam-analytics" className="grid mb-8">
                            <div className="col-12">
                                <div className="flex align-items-center justify-content-between mb-6">
                                    <h2 className="font-bold text-3xl" style={{ color: colors.textColor }}>
                                        System Overview
                                    </h2>
                                    <div className="flex align-items-center gap-2">
                                        <span className="text-sm" style={{ color: colors.lightText }}>Auto-refresh:</span>
                                        <i className="pi pi-circle-fill text-green-500 animate-pulse"></i>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Total Capacity Card */}
                            <div className="col-12 md:col-4 p-2">
                                <Card className="h-full border-round-xl shadow-2 hover:shadow-4 transition-all duration-300 cursor-pointer"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor,
                                          borderTop: `4px solid ${colors.primaryColor}`
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-6">
                                        <div className="p-3 border-round-lg" 
                                             style={{ 
                                                 backgroundColor: `${colors.primaryColor}15`,
                                                 color: colors.primaryColor 
                                             }}>
                                            <FaWater size={24} />
                                        </div>
                                        <Tag value="+2.0%" severity="success" rounded />
                                    </div>
                                    <div className="mb-4">
                                        <div className="text-5xl font-bold mb-1" style={{ color: colors.primaryColor }}>
                                            396K
                                        </div>
                                        <div className="text-sm font-medium" style={{ color: colors.textColor }}>
                                            Total Capacity
                                        </div>
                                        <div className="text-xs mt-1" style={{ color: colors.lightText }}>
                                            Combined capacity of all monitored dams
                                        </div>
                                    </div>
                                    <ProgressBar 
                                        value={3.14} 
                                        style={{ height: '10px', borderRadius: '10px' }}
                                        showValue={false}
                                    />
                                    <div className="flex justify-content-between text-sm mt-3">
                                        <span style={{ color: colors.lightText }}>Utilization</span>
                                        <span className="font-semibold" style={{ color: colors.primaryColor }}>3.14%</span>
                                    </div>
                                </Card>
                            </div>

                            {/* Daily Inflow Card */}
                            <div className="col-12 md:col-4 p-2">
                                <Card className="h-full border-round-xl shadow-2 hover:shadow-4 transition-all duration-300 cursor-pointer"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor,
                                          borderTop: `4px solid ${colors.successColor}`
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-6">
                                        <div className="p-3 border-round-lg" 
                                             style={{ 
                                                 backgroundColor: `${colors.successColor}15`,
                                                 color: colors.successColor 
                                             }}>
                                            <FaTachometerAlt size={24} />
                                        </div>
                                        <Tag value="Trending" severity="info" rounded />
                                    </div>
                                    <div className="mb-4">
                                        <div className="text-5xl font-bold mb-1" style={{ color: colors.successColor }}>
                                            340K
                                        </div>
                                        <div className="text-sm font-medium" style={{ color: colors.textColor }}>
                                            Daily Inflow
                                        </div>
                                        <div className="text-xs mt-1" style={{ color: colors.lightText }}>
                                            Total inflow across all dams
                                        </div>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <FaArrowUp className="text-green-500" />
                                        <span className="text-sm font-medium" style={{ color: colors.successColor }}>2.0% increase</span>
                                        <span className="text-xs ml-auto" style={{ color: colors.lightText }}>from yesterday</span>
                                    </div>
                                </Card>
                            </div>

                            {/* System Status Card */}
                            <div className="col-12 md:col-4 p-2">
                                <Card className="h-full border-round-xl shadow-2 hover:shadow-4 transition-all duration-300 cursor-pointer"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor,
                                          borderTop: `4px solid ${colors.warningColor}`
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-6">
                                        <div className="p-3 border-round-lg" 
                                             style={{ 
                                                 backgroundColor: `${colors.warningColor}15`,
                                                 color: colors.warningColor 
                                             }}>
                                            <FaChartLine size={24} />
                                        </div>
                                        <Badge value="2" severity="danger" />
                                    </div>
                                    <div className="text-center mb-6">
                                        <div className="text-5xl font-bold mb-1" style={{ color: getDamStatusColor(3.14) }}>
                                            3.14%
                                        </div>
                                        <div className="text-sm font-medium" style={{ color: colors.textColor }}>
                                            Average Dam Fullness
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="col-4 text-center">
                                            <div className="font-bold text-lg" style={{ color: colors.dangerColor }}>0</div>
                                            <div className="text-xs" style={{ color: colors.lightText }}>Critical</div>
                                        </div>
                                        <div className="col-4 text-center">
                                            <div className="font-bold text-lg" style={{ color: colors.warningColor }}>2</div>
                                            <div className="text-xs" style={{ color: colors.lightText }}>Low</div>
                                        </div>
                                        <div className="col-4 text-center">
                                            <div className="font-bold text-lg" style={{ color: colors.successColor }}>4</div>
                                            <div className="text-xs" style={{ color: colors.lightText }}>Normal</div>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>

                        {/* Enhanced Charts and Data Section */}
                        <div id="inflow-trends" className="grid mb-8">
                            {/* Inflow Contributions Table */}
                            <div className="col-12 lg:col-6 p-2">
                                <Card className="h-full border-round-xl shadow-2"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center gap-3">
                                            <div className="p-2 border-round-lg" 
                                                 style={{ 
                                                     backgroundColor: `${colors.infoColor}15`,
                                                     color: colors.infoColor 
                                                 }}>
                                                <FaDatabase size={20} />
                                            </div>
                                            <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                Inflow Contributions
                                            </h3>
                                        </div>
                                        <Button 
                                            icon="pi pi-ellipsis-v" 
                                            className="p-button-text p-button-rounded"
                                            style={{ color: colors.lightText }}
                                        />
                                    </div>
                                    <DataTable
                                        value={inflowContributions}
                                        size="small"
                                        className="p-datatable-sm"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Column 
                                            field="name" 
                                            header="Dam" 
                                            body={(rowData) => (
                                                <div className="flex align-items-center gap-2">
                                                    <GiWaterDrop style={{ color: colors.primaryColor }} />
                                                    <span style={{ color: colors.textColor }}>{rowData.name}</span>
                                                </div>
                                            )}
                                        />
                                        <Column 
                                            field="value" 
                                            header="Contribution" 
                                            body={(rowData) => (
                                                <div className="flex align-items-center gap-2">
                                                    {getTrendIcon(rowData.trend)}
                                                    <span className="font-semibold" style={{ color: colors.textColor }}>
                                                        {rowData.value}%
                                                    </span>
                                                </div>
                                            )}
                                        />
                                        <Column 
                                            field="volume" 
                                            header="Volume" 
                                            body={(rowData) => (
                                                <span className="font-medium" style={{ color: colors.lightText }}>
                                                    {rowData.volume}
                                                </span>
                                            )}
                                        />
                                    </DataTable>
                                    <div className="flex justify-content-between align-items-center mt-4 pt-3 border-top-1"
                                         style={{ borderColor: colors.borderColor }}>
                                        <span className="font-semibold" style={{ color: colors.textColor }}>Total</span>
                                        <span className="font-bold" style={{ color: colors.primaryColor }}>100.00%</span>
                                    </div>
                                </Card>
                            </div>

                            {/* Chart Card */}
                            <div className="col-12 lg:col-6 p-2">
                                <Card className="h-full border-round-xl shadow-2"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center gap-3">
                                            <div className="p-2 border-round-lg" 
                                                 style={{ 
                                                     backgroundColor: `${colors.primaryColor}15`,
                                                     color: colors.primaryColor 
                                                 }}>
                                                <FaChartLine size={20} />
                                            </div>
                                            <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                Capacity vs Inflow
                                            </h3>
                                        </div>
                                        <div className="flex gap-1">
                                            {['24h', '7d', '30d'].map(range => (
                                                <Button 
                                                    key={range}
                                                    label={range}
                                                    className={`p-button-text p-button-sm ${timeRange === range ? 'font-bold' : ''}`}
                                                    style={{ 
                                                        color: timeRange === range ? colors.primaryColor : colors.lightText
                                                    }}
                                                    onClick={() => setTimeRange(range)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ minHeight: '320px' }}>
                                        {chartData && (
                                            <Chart height="100%" type="bar" data={chartData} options={chartOptions} />
                                        )}
                                    </div>
                                </Card>
                            </div>
                        </div>

                        {/* Enhanced Maps Section */}
                        <div id="maps" className="grid mb-8">
                            <div className="col-12">
                                <Card className="border-round-xl shadow-2"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-6">
                                        <div className="flex align-items-center gap-3">
                                            <div className="p-2 border-round-lg" 
                                                 style={{ 
                                                     backgroundColor: `${colors.warningColor}15`,
                                                     color: colors.warningColor 
                                                 }}>
                                                <FaMapMarkedAlt size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                    Interactive Water Network Map
                                                </h3>
                                                <p className="text-sm mt-1 m-0" style={{ color: colors.lightText }}>
                                                    Real-time dam locations and status visualization
                                                </p>
                                            </div>
                                        </div>
                                        <Button 
                                            label="Full Screen" 
                                            icon="pi pi-external-link"
                                            className="p-button-outlined p-button-sm"
                                            style={{ 
                                                borderColor: colors.borderColor,
                                                color: colors.textColor
                                            }}
                                        />
                                    </div>
                                    <div className="border-round-lg overflow-hidden" 
                                         style={{ 
                                             height: '500px', 
                                             border: `1px solid ${colors.borderColor}`,
                                             borderRadius: '12px'
                                         }}>
                                        <DamsMap />
                                    </div>
                                    <div className="flex flex-wrap gap-4 mt-4 pt-4 border-top-1" style={{ borderColor: colors.borderColor }}>
                                        {dams.slice(1, 5).map(dam => (
                                            <div key={dam.code} className="flex align-items-center gap-2">
                                                <div className="w-3 h-3 border-round" style={{ backgroundColor: getDamStatusColor(dam.level?? undefined) }}></div>
                                                <span className="text-sm" style={{ color: colors.textColor }}>{dam.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </div>
                        </div>

                        {/* Enhanced Recent Activity Section */}
                        <div className="grid mb-8">
                            {/* Recent Readings */}
                            <div className="col-12 lg:col-8 p-2">
                                <Card className="h-full border-round-xl shadow-2"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center gap-3">
                                            <div className="p-2 border-round-lg" 
                                                 style={{ 
                                                     backgroundColor: `${colors.successColor}15`,
                                                     color: colors.successColor 
                                                 }}>
                                                <FaChartLine size={20} />
                                            </div>
                                            <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                Daily Performance Metrics
                                            </h3>
                                        </div>
                                        <Button 
                                            label="View History" 
                                            icon="pi pi-history"
                                            className="p-button-outlined p-button-sm"
                                            style={{ 
                                                borderColor: colors.borderColor,
                                                color: colors.textColor
                                            }}
                                        />
                                    </div>
                                    <DataTable
                                        value={dailyReadings}
                                        size="small"
                                        className="p-datatable-sm"
                                    >
                                        <Column 
                                            field="date" 
                                            header="Date" 
                                            body={(rowData) => (
                                                <span className="font-medium" style={{ color: colors.textColor }}>
                                                    {rowData.date}
                                                </span>
                                            )}
                                        />
                                        <Column 
                                            field="totalInflow" 
                                            header="Total Inflow" 
                                            body={(rowData) => (
                                                <div className="flex align-items-center gap-2">
                                                    {getTrendIcon(rowData.trend)}
                                                    <span className="font-bold" style={{ color: colors.textColor }}>
                                                        {formatNumber(rowData.totalInflow)} m³
                                                    </span>
                                                </div>
                                            )}
                                        />
                                        <Column 
                                            field="avgLevel" 
                                            header="Avg. Level" 
                                            body={(rowData) => (
                                                <div className="flex flex-column gap-1">
                                                    <div className="flex justify-content-between">
                                                        <span className="font-medium" style={{ color: getDamStatusColor(rowData.avgLevel) }}>
                                                            {rowData.avgLevel}%
                                                        </span>
                                                    </div>
                                                    <ProgressBar 
                                                        value={rowData.avgLevel} 
                                                        style={{ height: '6px', borderRadius: '3px' }}
                                                        showValue={false}
                                                    />
                                                </div>
                                            )}
                                        />
                                        <Column 
                                            field="alerts" 
                                            header="Alerts" 
                                            body={(rowData) => (
                                                rowData.alerts > 0 ? (
                                                    <Badge value={rowData.alerts} severity="danger" />
                                                ) : (
                                                    <Tag value="Clear" severity="success" rounded />
                                                )
                                            )}
                                        />
                                    </DataTable>
                                </Card>
                            </div>

                            {/* Recent Events Timeline */}
                            <div className="col-12 lg:col-4 p-2">
                                <Card className="h-full border-round-xl shadow-2"
                                      style={{ 
                                          backgroundColor: colors.cardBg, 
                                          borderColor: colors.borderColor 
                                      }}>
                                    <div className="flex align-items-center justify-content-between mb-4">
                                        <div className="flex align-items-center gap-3">
                                            <div className="p-2 border-round-lg" 
                                                 style={{ 
                                                     backgroundColor: `${colors.dangerColor}15`,
                                                     color: colors.dangerColor 
                                                 }}>
                                                <FaExclamationTriangle size={20} />
                                            </div>
                                            <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                                Recent Events
                                            </h3>
                                        </div>
                                        <Badge value={recentEvents.length} />
                                    </div>
                                    <div style={{ height: '300px', overflowY: 'auto' }}>
                                        <Timeline 
                                            value={recentEvents} 
                                            align="left" 
                                            content={eventTemplate}
                                            className="custom-timeline"
                                        />
                                    </div>
                                </Card>
                            </div>
                        </div>

                        {/* Enhanced CTA Section */}
                        <div className="text-center my-12 py-12 px-4 border-round-2xl shadow-3 relative overflow-hidden"
                             style={{ 
                                 backgroundColor: colors.cardBg,
                                 background: `linear-gradient(135deg, ${colors.primaryBg} 0%, ${colors.surfaceBg} 100%)`,
                                 border: `1px solid ${colors.borderColor}`
                             }}>
                            {/* Background pattern */}
                            <div className="absolute inset-0 opacity-5 pointer-events-none">
                                <div className="absolute top-0 left-0 w-32 h-32 border-2 border-blue-500 rounded-full -translate-x-16 -translate-y-16"></div>
                                <div className="absolute bottom-0 right-0 w-32 h-32 border-2 border-cyan-500 rounded-full translate-x-16 translate-y-16"></div>
                            </div>

                            <div className="relative z-10">
                                <div className="inline-flex align-items-center gap-2 px-4 py-2 border-round-full mb-6 shadow-2"
                                     style={{ 
                                         backgroundColor: colors.primaryColor,
                                         color: 'white'
                                     }}>
                                    <GiWaterDrop />
                                    <span className="font-bold">READY TO DEPLOY IN YOUR CITY?</span>
                                </div>
                                <h2 className="font-bold text-4xl mb-4" style={{ color: colors.textColor }}>
                                    Transform Your Water Management
                                </h2>
                                <p className="text-xl mb-8 mx-auto" style={{ 
                                    maxWidth: '700px', 
                                    color: colors.lightText,
                                    lineHeight: '1.8'
                                }}>
                                    This interactive demo showcases just a fraction of AmanziLytics' capabilities. 
                                    Experience real-time analytics, predictive insights, and intelligent water infrastructure management.
                                </p>
                                <div className="flex flex-wrap gap-4 justify-content-center">
                                    <Button 
                                        label="Schedule Full Demo" 
                                        className="font-bold px-8 py-3 shadow-2 hover:shadow-4 transition-all duration-300"
                                        style={{ 
                                            backgroundColor: colors.primaryColor, 
                                            borderColor: colors.primaryColor,
                                            fontSize: '1.1rem'
                                        }}
                                        icon="pi pi-calendar"
                                        size="large"
                                    />
                                    <Button 
                                        label="Contact Sales" 
                                        className="p-button-outlined font-bold px-8 py-3 hover:shadow-3 transition-all duration-300"
                                        style={{ 
                                            borderColor: colors.primaryColor, 
                                            color: colors.primaryColor,
                                            fontSize: '1.1rem'
                                        }}
                                        icon="pi pi-comments"
                                        size="large"
                                    />
                                    <Button 
                                        label="Technical Docs" 
                                        className="p-button-text font-bold px-8 py-3"
                                        style={{ color: colors.primaryColor }}
                                        icon="pi pi-book"
                                        size="large"
                                    />
                                </div>
                                <div className="mt-8 pt-8 border-top-1" style={{ borderColor: colors.borderColor }}>
                                    <p className="text-sm" style={{ color: colors.lightText }}>
                                        Join 50+ municipalities already optimizing their water management with AmanziLytics
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DemoDashboard;